import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import React from 'react';
import {
   AccountStore,
   Component,
   IssueActions,
   IssueStore,
   LocationStore,
   SettingsStore
} from '../../../client';
import { constants, lodash as _ } from '../../../common';
import LoadingBox from '../../../components/feedback/LoadingBox';
import IssueSort from '../../../components/issues/IssueSort';
import { AddCircleIcon } from '../../../components/icons';
import { Page, PageContent, PageHeader } from '../../../components/ux/Page';
import SearchBox from '../../../components/ux/SearchBox';
import './style.scss';

const { issueViewTypes, issueStates } = constants;
const PAGE_SIZE = 20;

const operationalViews = [
   issueViewTypes.TRIAGE,
   issueViewTypes.OVERDUE,
   issueViewTypes.UPCOMING_ISSUES,
   issueViewTypes.UNACTIONED_ISSUES,
   issueViewTypes.ASSIGNED,
   issueViewTypes.RAISED,
   issueViewTypes.WITHOUT_TAGS,
   issueViewTypes.WITHOUT_DEADLINE
];

const lifecycleViews = {
   OPEN: { caption: 'open issues', idIssueViewType: issueViewTypes.ALL.id, filters: [{ field: 'closed', value: false }] },
   UNASSIGNED: { caption: 'issues not assigned to anyone', idIssueViewType: issueViewTypes.UNASSIGNED.id, filters: [] },
   CLOSED: { caption: 'closed issues', idIssueViewType: issueViewTypes.CLOSED_ISSUES.id, filters: [] },
   ALL: { caption: 'all issues', idIssueViewType: issueViewTypes.ALL.id, filters: [] }
};

const formatDate = (value, fallback) => {
   if (!value) return fallback;
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? fallback : date.toLocaleDateString();
};

const displayId = (issue) => (issue.noIssue == null ? 'ISS-—' : `ISS-${String(issue.noIssue).padStart(3, '0')}`);

class IssuesPage extends Component {
   constructor(props) {
      super(props);
      this.stores = [IssueStore, LocationStore, SettingsStore, AccountStore];
      this.storeKeys = [
         'issues',
         'paging',
         'summary',
         'initialised',
         'loadingInitial',
         'refreshing',
         'loadingMore',
         'error',
         'filters',
         'locations',
         'priorities',
         'allTags',
         'issuesRefreshPending'
      ];
      this.state = { viewAnchor: null, searchDraft: '' };
      this.sentinelRef = React.createRef();
      this.searchTimer = null;
      this.observer = null;
   }

   componentDidMount() {
      if (super.componentDidMount) super.componentDidMount();
      const searchText = this.state.filters ? this.state.filters.searchText : '';
      this.setState({ searchDraft: searchText || '' }, () => {
         if (!this.state.initialised || this.state.issuesRefreshPending) this._fetchPage(1);
      });
      this._observeSentinel();
   }

   componentDidUpdate(prevProps, prevState) {
      if (super.componentDidUpdate) super.componentDidUpdate(prevProps, prevState);
      if (prevState.loadingInitial !== this.state.loadingInitial || prevState.loadingMore !== this.state.loadingMore) {
         this._observeSentinel();
      }
   }

   componentWillUnmount() {
      if (super.componentWillUnmount) super.componentWillUnmount();
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (this.observer) this.observer.disconnect();
   }

   _observeSentinel = () => {
      if (this.observer) this.observer.disconnect();
      if (!this.sentinelRef.current || typeof IntersectionObserver === 'undefined') return;
      this.observer = new IntersectionObserver(
         (entries) => {
            if (entries[0] && entries[0].isIntersecting) this._loadMore();
         },
         { rootMargin: '240px 0px' }
      );
      this.observer.observe(this.sentinelRef.current);
   };

   _buildRequest = (page) => {
      const { filters } = this.state;
      const operational = filters.operationalView == null ? null : _.find(operationalViews, { id: filters.operationalView });
      const lifecycle = lifecycleViews[filters.lifecycleView] || lifecycleViews.OPEN;
      const fieldFilters = [...(operational ? [] : lifecycle.filters)];

      if (filters.noPriority != null && filters.noPriority !== '') {
         fieldFilters.push({ field: 'noPriority', value: Number(filters.noPriority) });
      }

      return {
         idIssueViewType: operational ? operational.id : lifecycle.idIssueViewType,
         idIssueSortType: filters.idIssueSortType,
         searchText: filters.searchText,
         nosLocation: filters.nosLocation,
         filters: fieldFilters,
         page,
         limit: PAGE_SIZE
      };
   };

   _fetchPage = (page) => {
      const { loadingInitial, refreshing, loadingMore } = this.state;
      if (loadingInitial || refreshing || loadingMore) return;
      IssueActions.beginFetchIssuesSync({ page });
      IssueActions.fetchIssues(this._buildRequest(page));
   };

   _loadMore = () => {
      const { issues = [], paging = {}, loadingInitial, refreshing, loadingMore } = this.state;
      if (loadingInitial || refreshing || loadingMore || issues.length === 0 || issues.length >= (paging.total || 0)) return;
      this._fetchPage((paging.page || 1) + 1);
   };

   _setFilters = (changes) => {
      IssueActions.setIssueFiltersSync(changes);
      window.setTimeout(() => this._fetchPage(1), 0);
   };

   _setLifecycle = (lifecycleView) => {
      this.setState({ viewAnchor: null });
      this._setFilters({ lifecycleView, operationalView: null });
   };

   _setOperational = (view) => {
      this.setState({ viewAnchor: null });
      this._setFilters({ lifecycleView: 'OPEN', operationalView: view.id });
   };

   _onSearchChange = (searchDraft) => {
      this.setState({ searchDraft });
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this._setFilters({ searchText: searchDraft }), 300);
   };

   _onSortChange = (idIssueSortType) => {
      this._setFilters({ idIssueSortType });
   };

   _onLocationChange = (event) => {
      const value = event.target.value;
      this._setFilters({ nosLocation: value === '' ? [] : [Number(value)] });
   };

   _onFlagChange = (event) => {
      const value = event.target.value;
      this._setFilters({ noPriority: value === '' ? null : Number(value) });
   };

   _effectiveCaption = () => {
      const { filters } = this.state;
      const operational = filters.operationalView == null ? null : _.find(operationalViews, { id: filters.operationalView });
      return operational ? operational.name : (lifecycleViews[filters.lifecycleView] || lifecycleViews.OPEN).caption;
   };

   _status = (issue) => {
      const state = issue.state || 0;
      const closed = (state & issueStates.CLOSED) === issueStates.CLOSED;
      const assigned = (state & issueStates.ASSIGNED) === issueStates.ASSIGNED;
      if (closed) return { caption: 'Closed', className: 'closed' };
      if (!assigned) return { caption: 'Unassigned', className: 'unassigned' };
      return { caption: 'Open', className: 'open' };
   };

   _locationCaption = (issue) => {
      const noLocation = issue.where && issue.where.noLocation;
      const location = _.find(this.state.locations || [], (item) => String(item.no) === String(noLocation));
      return (location && (location.fullName || location.name)) || (noLocation == null ? 'No location' : `Location #${noLocation}`);
   };

   _ownerCaption = (issue) => {
      const who = issue.who || {};
      if (who.assignees) return who.assignees;
      if (who.caption) return who.caption;
      if (who.staff && who.staff.length) return who.staff.length === 1 ? `Staff #${who.staff[0].no}` : `${who.staff.length} staff`;
      if (who.teams && who.teams.length) return who.teams.length === 1 ? `Team #${who.teams[0].no}` : `${who.teams.length} teams`;
      if (who.positions && who.positions.length) return who.positions.length === 1 ? `Position #${who.positions[0].no}` : `${who.positions.length} positions`;
      return 'Unassigned';
   };

   _flag = (issue) => {
      if (issue.noPriority == null) return null;
      return _.find(this.state.priorities || [], (flag) => String(flag.no) === String(issue.noPriority)) || {
         no: issue.noPriority,
         name: `Priority #${issue.noPriority}`,
         color: '#607d8b'
      };
   };

   _tags = (issue) => {
      const allTags = this.state.allTags || [];
      return _.map(issue.tags || [], (tag) => {
         const known = _.find(allTags, (candidate) => String(candidate.noTag || candidate.no) === String(tag.noTag));
         return { noTag: tag.noTag, name: (known && known.name) || tag.name || `Tag #${tag.noTag}` };
      });
   };

   _openIssue = (issue) => {
      if (issue && issue.noIssue != null) {
         this.props.history.push(`/app/observe/issues/${issue.noIssue}`);
      }
   };

   _onIssueKeyDown = (event, issue) => {
      if (event.key === 'Enter') this._openIssue(issue);
   };

   _renderRow = (issue) => {
      const status = this._status(issue);
      const flag = this._flag(issue);
      const tags = this._tags(issue);
      const due = issue.dueBy || (issue.timing && issue.timing.dueBy);
      const raised = issue.raisedOn || issue.ctd;

      return (
         <div
            className='issue-row'
            key={issue.id || issue.noIssue}
            role='link'
            tabIndex={0}
            onClick={() => this._openIssue(issue)}
            onKeyDown={(event) => this._onIssueKeyDown(event, issue)}>
            <div className='issue-main'>
               <div className='issue-id'>{displayId(issue)}</div>
               <div className='issue-title'>{issue.title || 'Untitled issue'}</div>
               <div className='issue-tags'>
                  {tags.slice(0, 2).map((tag) => <span className='issue-tag' key={tag.noTag}>{tag.name}</span>)}
                  {tags.length > 2 && <span className='issue-tag-more'>+{tags.length - 2}</span>}
               </div>
            </div>
            <div>{this._locationCaption(issue)}</div>
            <div>{this._ownerCaption(issue)}</div>
            <div>{flag ? <span className='issue-priority' style={{ borderColor: flag.color || '#607d8b' }}>{flag.name}</span> : <span className='muted'>No priority</span>}</div>
            <div>{formatDate(due, 'No deadline')}</div>
            <div>{formatDate(raised, 'Unknown')}</div>
            <div><span className={`issue-status ${status.className}`}>{status.caption}</span></div>
         </div>
      );
   };

   render() {
      const {
         issues = [], paging = {}, summary = {}, initialised, loadingInitial, refreshing, loadingMore, error,
         filters = {}, locations = [], priorities = [], searchDraft, viewAnchor
      } = this.state;
      const selectedLocation = filters.nosLocation && filters.nosLocation.length ? filters.nosLocation[0] : '';
      const hasLoaded = initialised && !loadingInitial;

      return (
         <div className='app-wrapper IssuesPage'>
            <Page>
               <PageHeader
                  title='Issues'
                  componentTopRight={
                     <IssueSort
                        idIssueSortType={filters.idIssueSortType}
                        onChange={this._onSortChange}
                     />
                  }
                  componentLeft={
                     <Button
                        id='raise-issue-button'
                        variant='contained'
                        color='primary'
                        className='btn transform-none'
                        onClick={() => this.props.history.push('/app/observe/issues/new')}>
                        <AddCircleIcon className='ico' />
                        Raise issue
                     </Button>
                  }
                  bar
                  componentBottom={
                     <>
                        <div className='issues-toolbar'>
                           <span>showing</span>
                           <Button className='view-button' onClick={(event) => this.setState({ viewAnchor: event.currentTarget })}>
                              {this._effectiveCaption()}
                           </Button>
                           <Menu anchorEl={viewAnchor} keepMounted open={Boolean(viewAnchor)} onClose={() => this.setState({ viewAnchor: null })}>
                              <MenuItem disabled>Lifecycle</MenuItem>
                              <MenuItem onClick={() => this._setLifecycle('OPEN')}>Open issues</MenuItem>
                              <MenuItem onClick={() => this._setLifecycle('UNASSIGNED')}>Unassigned issues</MenuItem>
                              <MenuItem onClick={() => this._setLifecycle('CLOSED')}>Closed issues</MenuItem>
                              <MenuItem onClick={() => this._setLifecycle('ALL')}>All issues</MenuItem>
                              <MenuItem disabled>Operational views</MenuItem>
                              {operationalViews.map((view) => <MenuItem key={view.id} onClick={() => this._setOperational(view)}>{view.name}</MenuItem>)}
                           </Menu>
                        </div>
                        {hasLoaded && (
                           <div className='issue-summary'>
                              <button className={filters.lifecycleView === 'OPEN' ? 'active' : ''} onClick={() => this._setLifecycle('OPEN')}>Open {summary.open || 0}</button>
                              <span>·</span>
                              <button className={filters.lifecycleView === 'UNASSIGNED' ? 'active' : ''} onClick={() => this._setLifecycle('UNASSIGNED')}>Unassigned {summary.unassigned || 0}</button>
                              <span>·</span>
                              <button className={filters.lifecycleView === 'CLOSED' ? 'active' : ''} onClick={() => this._setLifecycle('CLOSED')}>Closed {summary.closed || 0}</button>
                              <span>·</span>
                              <button className={filters.lifecycleView === 'ALL' ? 'active' : ''} onClick={() => this._setLifecycle('ALL')}>All {summary.total || 0}</button>
                           </div>
                        )}
                        <div className='issues-controls'>
                           <TextField
                              select
                              label='Location'
                              value={selectedLocation}
                              onChange={this._onLocationChange}
                              InputLabelProps={{ shrink: true }}
                              SelectProps={{ native: true }}
                              variant='outlined'
                              margin='dense'>
                              <option value=''>All locations</option>
                              {locations.map((location) => <option key={location.no} value={location.no}>{location.fullName || location.name}</option>)}
                           </TextField>
                           <div className='issues-search'><SearchBox placeholder='Search issues' searchText={searchDraft} onChange={this._onSearchChange} /></div>
                           <TextField
                              select
                              label='Priority'
                              value={filters.noPriority == null ? '' : filters.noPriority}
                              onChange={this._onFlagChange}
                              InputLabelProps={{ shrink: true }}
                              SelectProps={{ native: true }}
                              variant='outlined'
                              margin='dense'>
                              <option value=''>All priorities</option>
                              {_.sortBy(priorities, ['order']).map((flag) => <option key={flag.no} value={flag.no}>{flag.name}</option>)}
                           </TextField>
                        </div>
                        <div className='issue-list-header'>
                           <div>Issue</div><div>Location</div><div>Assigned to</div><div>Priority</div><div>Due</div><div>Raised</div><div>Status</div>
                        </div>
                     </>
                  }
               />
               <PageContent margin='narrow'>
                  <LoadingBox loadingTypes={[]}>
                     {error && <div className='issues-message error'>{error}</div>}
                     {loadingInitial && <div className='issues-message'>Loading issues…</div>}
                     {refreshing && <div className='issues-refreshing'>Refreshing issues…</div>}
                     {!loadingInitial && !error && !refreshing && issues.length === 0 && <div className='issues-message'>No issues found.</div>}
                     {!loadingInitial && issues.map(this._renderRow)}
                     {loadingMore && <div className='issues-message'>Loading more issues…</div>}
                     {!loadingInitial && !loadingMore && issues.length > 0 && issues.length >= (paging.total || 0) && <div className='issues-end'>End of issues</div>}
                     <div className='issues-sentinel' ref={this.sentinelRef} />
                  </LoadingBox>
               </PageContent>
            </Page>
         </div>
      );
   }
}

export default IssuesPage;
